/* eslint-disable @typescript-eslint/no-explicit-any */
import { NodeType } from 'shared/types/ingestion'
import { IConfig, LessonStructure } from '../../types'
import { BaseLessonConfig } from '../lessonConfig'
import {
  createActivity,
  createNodeList,
  createNoteNode,
  createShortAnswer,
  createSkillsList,
} from 'ingestion/nodeCreationHelpers'
import {
  IInlineNode,
  INoteNode,
  IQuestionNode,
  ISimplifiedNode,
} from 'components/ingestion/IngestedTree'
import {
  cleanListElement,
  convertToCleanString,
  convertToKiddomHTMLStyle,
} from '../../utilities/utility'

import { extractSkills } from './extractSkills'
import { getSkills } from './getSkills'
import { createNoteWithConfig } from './createNoteWithConfig'
import { getQuestionsPracticeProblems } from './getQuestionsPracticeProblems'
import capitalize from 'lodash.capitalize'

export function getQuestions(
  configKey: string,
  config: any,
  lesson: LessonStructure,
  name?: string,
): ISimplifiedNode[] {
  if (config.practiceProblems) {
    return getQuestionsPracticeProblems(configKey, config, lesson, name)
  }
  const questionNodes: ISimplifiedNode[] = []

  const descriptionBase = getQuestionDescription(
    configKey,
    config,
    lesson,
    name,
  )
  const teacherNotesBase = getQuestionTeacherNotes(
    configKey,
    config,
    lesson,
    name,
  )

  const descriptionsP = descriptionBase.match(/(<p>\d+\.)/g)
  let prevIndex = descriptionBase.length
  const descriptions = descriptionsP
    ? descriptionsP
        .reverse()
        .map((dP, index) => {
          const i = descriptionBase.indexOf(dP)
          if (index === descriptionsP.length - 1) {
            return descriptionBase.slice(0, prevIndex)
          }
          const description = descriptionBase.slice(i, prevIndex)
          prevIndex = i
          return description
        })
        .filter((d) => !!d)
        .reverse()
    : [descriptionBase]

  const teacherNotesP = teacherNotesBase.match(/(<p>\d+\.)/g)
  prevIndex = descriptionBase.length
  const teacherNotes = teacherNotesP
    ? teacherNotesP
        .reverse()
        .map((dP, index) => {
          const i = teacherNotesBase.indexOf(dP)
          if (index === teacherNotesP.length - 1) {
            return teacherNotesBase.slice(0, prevIndex)
          }
          const teacherNote = teacherNotesBase.slice(i - 1, prevIndex)
          prevIndex = i
          return teacherNote
        })
        .filter((d) => !!d)
        .reverse()
    : [teacherNotesBase]
  descriptions.forEach((description, index) => {
    const teacherNote = teacherNotes[index] || ''
    const multiParts = description.match(/<ol(.*?)<\/ol>/g)
    if (multiParts) {
      const descParts = description.match(/<li(.*?)<\/li>/g)
      const tNotesMatchesLi = teacherNote.matchAll(/<li(.*?)<\/li>/g)
      const tNotesPartsLi = []
      for (const t of tNotesMatchesLi) {
        tNotesPartsLi.push(t[1])
      }
      const tNotesMatchesP = teacherNote.matchAll(/[<p>|\s][a-z]\.(.*?)<\/p>/g)
      const tNotesPartsP = []
      for (const t of tNotesMatchesP) {
        tNotesPartsP.push(t[1])
      }
      const tNotesParts =
        tNotesPartsLi.length > tNotesPartsP.length
          ? tNotesPartsLi
          : tNotesPartsP
      const mainDescription = description.match(/<p(.*?)<ol/g)

      const questionNode = createMultiPartQuestion(
        mainDescription ? mainDescription[0] : '',
        `Question ${index + 1}`,
        descParts?.map((d, i) =>
          createShortAnswer({
            description: cleanListElement(d) || '',
            title: `${i}.`,
            teacherNote:
              tNotesParts && tNotesParts[i]
                ? cleanListElement(tNotesParts[i]?.trim())
                : teacherNote,
          }),
        ) as IQuestionNode[],
      )
      questionNodes.push(questionNode)
      return
    }
    const questionNode = createShortAnswer({
      description: description || '',
      title: `Question ${index + 1}`,
      teacherNote,
    })
    questionNodes.push(questionNode)
  })

  return questionNodes
}

export function getQuestionDescription(
  configKey: string,
  config: any,
  lesson: LessonStructure,
  name?: string,
): string {
  const questionDescription = getContentFromConfig({
    lesson,
    config,
    name,
  })
  return (
    convertToKiddomHTMLStyle({ content: questionDescription })?.innerHTML || ''
  )
}

export function getQuestionTeacherNotes(
  configKey: string,
  config: any,
  lesson: LessonStructure,
  name?: string,
): string {
  const field = 'teacherNotes'
  const teacherNotes = getContentFromConfig({ lesson, config, name }, field)
  return convertToKiddomHTMLStyle({ content: teacherNotes })?.innerHTML || ''
}

async function createActivityNode({
  nodeConfig,
  lesson,
  name,
}: {
  nodeConfig: IConfig
  lesson: LessonStructure
  name?: string
}) {
  const nodes: { nodes: ISimplifiedNode[]; index: number }[] = []
  if (nodeConfig.config) {
    await Promise.all(
      Object.entries(nodeConfig.config).map(
        async ([configKey, config], index) => {
          if (config.type === NodeType.Note) {
            nodes.push({
              nodes: [createNoteWithConfig(configKey, config, lesson, name)],
              index,
            })
          }
          if (config.type === NodeType.SkillsList) {
            nodes.push({
              nodes: [
                createSkillsList(
                  configKey,
                  await getSkills(config, lesson, name),
                ),
              ],
              index,
            })
          }
          if (config.type === NodeType.Question) {
            nodes.push({
              nodes: getQuestions(configKey, config, lesson, name),
              index,
            })
          }
        },
      ),
    )
  }
  const activity = lesson.parts.find(
    (part) =>
      part.name?.toUpperCase() === nodeConfig.title?.toUpperCase() ||
      part.name?.toUpperCase() === name?.toUpperCase(),
  )
  const titlePart = activity?.parts?.find(
    (part: { name?: string }) => part?.name?.toUpperCase() === 'TITLE',
  )
  const title = titlePart?.content && convertToCleanString(titlePart as any)

  const activityNodes = nodes
    .sort((a, b) => a.index - b.index)
    .flatMap((node) => node.nodes)
  return createActivity(
    {
      title: titlePart
        ? `${(name && capitalize(name)) || nodeConfig.title}: ${title}`
        : (name && capitalize(name)) || nodeConfig.title || '',
      isAssignable: true,
      points: 4,
      assessment_grading: 'formative',
    },
    activityNodes,
  )
}

async function getNodeListNodes(
  config: IConfig,
  lesson: LessonStructure,
): Promise<ISimplifiedNode[]> {
  const nodes: { activityNode: ISimplifiedNode; index: number }[] = []
  if ('nodes' in config && config.nodes) {
    await Promise.all(
      config.nodes.map(async (nodeConfig: any, index) => {
        if (nodeConfig.type === NodeType.Activity) {
          if (nodeConfig.multiple) {
            const activities = lesson.parts.filter((part) =>
              part.name?.includes(nodeConfig.title.toUpperCase()),
            )
            const activityNodes = await Promise.all(
              activities.map(async (activity) => {
                const activityNode = await createActivityNode({
                  nodeConfig,
                  name: activity.name,
                  lesson,
                })
                return {
                  activityNode,
                  index,
                }
              }),
            )
            nodes.push(...activityNodes)
          } else {
            nodes.push({
              activityNode: await createActivityNode({ nodeConfig, lesson }),
              index,
            })
          }
        }
      }),
    )
  }
  nodes.sort((a, b) => a.index - b.index)
  return nodes.map((node) => node.activityNode)
}

export async function getLessonNodes(
  lesson: LessonStructure,
): Promise<ISimplifiedNode[]> {
  const nodes: { node: ISimplifiedNode; index: number }[] = []
  await Promise.all(
    Object.entries(BaseLessonConfig).map(async ([configKey, config], index) => {
      if (config.type === NodeType.Note) {
        nodes.push({
          node: createNoteWithConfig(configKey, config, lesson),
          index,
        })
      }
      if (config.type === NodeType.NodeList) {
        const nodelistChildren: ISimplifiedNode[] = config.nodes
          ? await getNodeListNodes(config, lesson)
          : []
        nodes.push({
          node: createNodeList(
            {
              title: configKey,
            },
            nodelistChildren,
          ),
          index,
        })
      }
      if (config.type === NodeType.SkillsList) {
        const skillList = createSkillsList(
          configKey,
          await getSkills(config, lesson),
        )
        nodes.push({
          node: skillList,
          index,
        })
      }
    }),
  )
  nodes.sort((a, b) => a.index - b.index)
  return nodes.map((node) => node.node)
}

export function getContentFromConfig(
  {
    lesson,
    config,
    name,
  }: {
    lesson: LessonStructure
    config: IConfig
    name?: string
  },
  field: 'includes' | 'teacherNotes' = 'includes',
): string {
  const fieldConfig = config[field]

  if (!fieldConfig || fieldConfig.length === 0) {
    return ''
  }

  for (const configPath of fieldConfig) {
    if (!configPath) continue

    const depth = configPath.split('|')

    // Top-level part match
    if (depth.length === 1) {
      const part = lesson.parts.find((part) => {
        return name
          ? part.name?.toUpperCase() === name.toUpperCase()
          : part.name?.toUpperCase().includes(configPath.toUpperCase())
      })
      if (part?.content) {
        return part.content
      }
    }

    // Nested part match
    if (depth.length > 1) {
      const d0 = lesson.parts.find((part) => {
        return name
          ? part.name?.toUpperCase() === name.toUpperCase()
          : part.name?.toUpperCase().includes(depth[0].toUpperCase())
      })

      if (!d0 || !d0.parts) {
        continue
      }

      const d1 = d0.parts.find((part) =>
        part.name?.toUpperCase().includes(depth[1].toUpperCase()),
      )

      if (d1?.content) {
        return d1.content
      }
    }
  }

  // Fallback if no matches found
  return ''
}

export function createMultiPartQuestion(
  questionText: string,
  title: string,
  questions: (IQuestionNode | INoteNode)[],
): IInlineNode {
  return {
    type: NodeType.Inline,
    data: {
      title,
      studentFacing: true,
      type: NodeType.MultiPartQuestion,
    },
    nodes: [
      createNoteNode({
        title: 'Description',
        value: questionText,
        studentFacing: true,
      }),
      ...questions,
    ],
  }
}

export {
  extractSkills,
  getSkills,
  createNoteNode,
  getQuestionsPracticeProblems,
}
